import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type * as Y from 'yjs'
import {
  boundingRect,
  formatBytes,
  type BoardElement,
  type Frame,
  type Language,
  type MediaDto,
  type SnapshotDto,
  type Translator,
} from '@praesto/shared'
import { api } from '../api.js'
import { Dialog } from './Dialog.js'
import { CameraStore } from '../canvas/CameraStore.js'
import { BoardLayer } from '../canvas/BoardLayer.js'
import { elementsOf } from '../collab/elements.js'
import { framesOf } from '../collab/frames.js'
import { docFromSnapshot, restoreSnapshot } from '../collab/restore.js'
import { useLanguage, useRichText, useText } from '../i18n.js'

/** „Heute, 14:32" statt eines vollen Datums, solange es dasselbe Datum ist. */
function formatMoment(iso: string, t: Translator, language: Language): string {
  const locale = language === 'de' ? 'de-DE' : 'en-GB'
  const date = new Date(iso)
  const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  const today = new Date()
  const sameDay = date.toDateString() === today.toDateString()
  if (sameDay) return t('history.today', { time })

  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return t('history.yesterday', { time })

  const day = date.toLocaleDateString(locale, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  })
  return t('history.on', { day, time })
}

/**
 * Der Verlauf eines Boards: Stände ansehen und einen davon zurückholen.
 *
 * Die Vorschau ist kein Bild, sondern derselbe Renderer mit einem zweiten
 * Dokument — dasselbe Verfahren wie bei der Gast-Vorschau im Vortrag. Ein
 * gespeichertes Vorschaubild je Stand wäre eine zweite Wahrheit, die altert.
 */
export function HistoryDialog({
  boardId,
  doc,
  mediaById,
  background,
  onClose,
  onRestored,
}: {
  boardId: string
  doc: Y.Doc
  mediaById: Map<string, MediaDto>
  background: string
  onClose: () => void
  onRestored: () => void
}): React.ReactElement {
  const t = useText()
  const rich = useRichText()
  const { language } = useLanguage()
  const [list, setList] = useState<SnapshotDto[] | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [content, setContent] = useState<{ elements: BoardElement[]; frames: Frame[] } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const camera = useMemo(() => new CameraStore(), [])
  const screenRef = useRef<HTMLDivElement>(null)
  const layerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api
      .history(boardId)
      .then((entries) => {
        setList(entries)
        setChosen(entries[0]?.id ?? null)
      })
      .catch(() => setError(t('history.loadListFailed')))
  }, [boardId, t])

  // Den gewählten Stand holen und in ein Wegwerf-Dokument legen.
  useEffect(() => {
    if (!chosen) return
    let cancelled = false
    setContent(null)
    api
      .snapshotState(boardId, chosen)
      .then((state) => {
        if (cancelled) return
        const past = docFromSnapshot(state)
        setContent({ elements: elementsOf(past), frames: framesOf(past) })
        past.destroy()
      })
      .catch(() => {
        if (!cancelled) setError(t('history.loadFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [boardId, chosen, t])

  useLayoutEffect(() => {
    const screen = screenRef.current
    if (!screen) return
    camera.attachLayer(layerRef.current)
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      camera.setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    observer.observe(screen)
    camera.setSize({ width: screen.clientWidth, height: screen.clientHeight })
    return () => {
      observer.disconnect()
      camera.attachLayer(null)
    }
  }, [camera, content !== null])

  useEffect(() => {
    if (!content) return
    const bounds = boundingRect([...content.frames, ...content.elements])
    if (bounds) camera.animateToRect(bounds, 1.1, 0)
  }, [camera, content])

  async function restore(): Promise<void> {
    if (!chosen) return
    setBusy(true)
    setError(null)
    try {
      // Erst den jetzigen Stand sichern: Auch die Wiederherstellung soll sich
      // zurücknehmen lassen.
      await api.saveSnapshot(boardId, t('history.beforeRestore'))
      const state = await api.snapshotState(boardId, chosen)
      restoreSnapshot(doc, state)
      onRestored()
      onClose()
    } catch {
      setError(t('history.restoreFailed'))
      setBusy(false)
    }
  }

  const current = list?.find((entry) => entry.id === chosen)

  return (
    <Dialog title={t('history.title')} onClose={onClose}>
      {error && <p className="form-error">{error}</p>}

      {list === null && <p className="muted">{t('history.loadingList')}</p>}

      {list?.length === 0 && (
        <p className="muted">{t('history.empty')}</p>
      )}

      {list && list.length > 0 && (
        <div className="history">
          <ol className="history-list">
            {list.map((entry) => (
              <li key={entry.id}>
                <button
                  className={`history-entry${entry.id === chosen ? ' is-active' : ''}`}
                  onClick={() => setChosen(entry.id)}
                >
                  <span className="history-when">
                    {formatMoment(entry.createdAt, t, language)}
                  </span>
                  <span className="muted small">
                    {entry.label ?? formatBytes(entry.byteSize, language)}
                  </span>
                </button>
              </li>
            ))}
          </ol>

          <div className="history-preview">
            <div
              ref={screenRef}
              className="history-screen"
              style={{ backgroundColor: background }}
            >
              {content ? (
                <BoardLayer
                  ref={layerRef}
                  camera={camera}
                  elements={content.elements}
                  frames={content.frames}
                  mediaById={mediaById}
                  selection={null}
                  canEdit={false}
                />
              ) : (
                <p className="muted small history-loading">{t('history.loadingState')}</p>
              )}
            </div>

            <p className="muted small">
              {content
                ? t('history.counts', {
                    elements: content.elements.length,
                    frames: content.frames.length,
                  })
                : ' '}
            </p>

            {confirming ? (
              <div className="history-confirm">
                <p className="small">
                  {rich('history.confirm', {
                    moment: (
                      <strong>
                        {current ? formatMoment(current.createdAt, t, language) : ''}
                      </strong>
                    ),
                  })}
                </p>
                <button className="button ghost" onClick={() => setConfirming(false)}>
                  {t('common.cancel')}
                </button>
                <button className="button primary" disabled={busy} onClick={() => void restore()}>
                  {busy ? t('history.restoring') : t('history.restoreConfirm')}
                </button>
              </div>
            ) : (
              <button
                className="button primary"
                disabled={!content || busy}
                onClick={() => setConfirming(true)}
              >
                {t('history.restore')}
              </button>
            )}
          </div>
        </div>
      )}
    </Dialog>
  )
}
